import { useState } from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { ProcessingActivityJointDataControllerPermissions } from "../../../auth/userContextProvider/UserContextProvider.types";
import { deleteHttp, post, put } from "../../../common/api/apiShared";
import { useExpandedId } from "../../../common/hooks/useExpandedId";
import { ValidationError } from "../../../common/validationError";
import { useSelectableLegalEntityQuery } from "../../../legalEntity/useSelectableLegalEntityQuery";
import { useTranslation } from "../../../localization/useTranslation";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../../user/User.types";
import { useSelectableDataCategoriesQuery } from "../../hooks/useSelectableDataCategoriesQuery";
import { useSelectableDocumentsQuery } from "../../hooks/useSelectableDocumentQuery";
import { JointDataController, ProcessingActivityModel, SelectableAgreement } from "../../ProcessingActivity.types";
import { AgreementSaveModel, DataProcessorAgreementSaveModel } from "../agreementDialog/DataProcessorAgreementDialog.types";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { useGroupEntitiesQuery } from "../../hooks/useGroupEntitiesQuery.ts";
import { ContractOnTransfer, SelectableContract, toSelectableAgreement } from "../../../contract/contract/Contract.types.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useJointDataControllerStep(processingActivity: ProcessingActivityModel, jointDataControllers: Array<JointDataController>) {
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();

    const optimisticUpdate = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();

    let queryKey = "processingActivity" + processingActivity.id;

    const jointDataControllerPermissions = permissions.processingActivityJointDataControllerPermissions;

    const [agreementSaveModel, setAgreementSaveModel] = useState<DataProcessorAgreementSaveModel | undefined>(undefined);
    const [jointDataControllerId, setJointDataControllerId] = useState("");
    const { getExpandedId, setExpandedId } = useExpandedId();

    let documentsQuery = useSelectableDocumentsQuery(
        jointDataControllerPermissions.hasAgreementPermission,
        jointDataControllers.find((x) => x.id === getExpandedId())?.dataControllerId ?? undefined,
        processingActivity.id
    );
    let responsibleQuery = useResponsibleQuery(jointDataControllerPermissions.hasResponsiblePermission);
    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    let legalEntitiesQuery = useSelectableLegalEntityQuery();
    let groupEntityQuery = useGroupEntitiesQuery();

    const legalEntityIdOnTransfer = processingActivity.jointDataControllers.find((x) => x.id === getExpandedId())?.dataControllerId ?? undefined;

    const contractsOnJointDataControllerUrl = `/ProcessingActivity/JointDataController/${getExpandedId()}/contracts`;
    const contractsOnJointDataControllerQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnJointDataControllerUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });
    const selectableContractsUrl = `/LegalEntity/${legalEntityIdOnTransfer}/selectableContracts`;
    const selectableContractsQuery = useDotLegalQuery<Array<SelectableContract>>(selectableContractsUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });

    let dataCategoriesData;
    if (dataCategoriesQuery.allDataCategories) {
        dataCategoriesData = dataCategoriesQuery.allDataCategories!.filter((item) =>
            processingActivity.dataCategories.some((dc) => dc.dataCategoryId === item.id)
        );
    }

    let documentsLoading = documentsQuery.isLoading;
    let documentsData: Array<SelectableAgreement> = documentsQuery.jointDataControllerDocuments.map((d) => {
        return {
            id: d.id,
            name: d.name,
            contractOrDocumentType: d.documentTypeName,
            status: d.status,
            type: "document",
        };
    });

    let groupEntityLoading = groupEntityQuery.isLoading;
    let groupEntityData = groupEntityQuery.allGroupEntities;

    let legalEntityLoading = legalEntitiesQuery.isLoading;
    let legalEntityData = legalEntitiesQuery.data;

    let responsibleLoading = responsibleQuery.isLoading;
    let responsibleData = responsibleQuery.data;

    const removeAgreementMutation = useDotLegalMutation(removeAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });
    const addAgreementMutation = useDotLegalMutation(addAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });

    const onAddAgreement = async (agreement: SelectableAgreement, jointDataControllerId: string) => {
        const agreementId = agreement.id;
        if (agreement.type === "document") {
            const newProcessingActivityModel = { ...processingActivity };
            const jointDataController = newProcessingActivityModel.jointDataControllers.find((d) => d.id === jointDataControllerId)!;

            jointDataController.documents.push(agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                jointDataControllerId: jointDataControllerId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type === "contract") {
            const contractsOnTransfer = contractsOnJointDataControllerQuery.data!;
            contractsOnTransfer.contractIds.push(agreementId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnJointDataControllerUrl, addContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: agreementId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onDeleteAgreement = async (agreement: SelectableAgreement, jointDataControllerId: string) => {
        const agreementId = agreement.id;
        if (agreement.type === "document") {
            const newProcessingActivityModel = { ...processingActivity };
            const jointDataController = newProcessingActivityModel.jointDataControllers.find((d) => d.id === jointDataControllerId)!;

            jointDataController.documents = jointDataController.documents.filter((x) => x !== agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeAgreementMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                jointDataControllerId: jointDataControllerId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type === "contract") {
            const contractsOnTransfer = contractsOnJointDataControllerQuery.data!;
            contractsOnTransfer.contractIds = contractsOnTransfer.contractIds.filter((x) => x !== agreementId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnJointDataControllerUrl, removeContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: agreementId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onHasAgreementChange = async (hasAgreement: boolean, jointDataControllerId: string) => {
        const newProcessingActivityModel = { ...processingActivity };
        const jointDataController = newProcessingActivityModel.jointDataControllers.find((d) => d.id === jointDataControllerId)!;

        jointDataController.hasAgreement = hasAgreement;
        jointDataController.documents = [];

        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
            processingActivityId: processingActivity.id,
            jointDataControllerId: jointDataControllerId,
            saveModel: { hasAgreement: hasAgreement },
        });
    };

    const selectableContracts: Array<SelectableAgreement> = selectableContractsQuery.data?.map((c) => toSelectableAgreement(c)) ?? [];

    const onLegalEntityOnTransferChange = () => {
        if (contractsOnJointDataControllerQuery.data) {
            const newData = { ...contractsOnJointDataControllerQuery.data };
            newData.contractIds = [];
            queryClient.setQueryData(contractsOnJointDataControllerUrl, newData);
        }
    };

    return {
        getExpandedId,
        setExpandedId,
        legalEntityData,
        legalEntityLoading,
        responsibleLoading,
        responsibleData,
        dataCategoriesData,
        groupEntityLoading,
        groupEntityData,
        jointDataControllerId,
        setJointDataControllerId,
        agreementSaveModel,
        setAgreementSaveModel,
        agreementsLoading: documentsLoading || contractsOnJointDataControllerQuery.isLoading || selectableContractsQuery.isLoading,
        selectableAgreements: [...documentsData, ...selectableContracts].sort((a, b) => a.name.localeCompare(b.name)),
        refetchDocuments: documentsQuery.refetch,
        onAddAgreement,
        onDeleteAgreement,
        onHasAgreementChange,
        selectedAgreements: [
            ...(processingActivity.jointDataControllers.find((d) => d.id === getExpandedId())?.documents ?? []),
            ...(contractsOnJointDataControllerQuery.data?.contractIds ?? []),
        ],
        containsNonAccessibleContracts: contractsOnJointDataControllerQuery.data?.containsNonAccessibleContacts ?? false,
        onLegalEntityOnTransferChange,
    };

    async function addAgreementAPI(data: { processingActivityId: string; jointDataControllerId: string; saveModel: AgreementSaveModel }) {
        return await put(
            "/processingactivity/" + data.processingActivityId + "/jointDataController/" + data.jointDataControllerId + "/agreement",
            data.saveModel
        );
    }

    async function removeAgreementAPI(data: { processingActivityId: string; jointDataControllerId: string; saveModel: AgreementSaveModel }) {
        return await deleteHttp(
            `/processingactivity/${data.processingActivityId}/jointDataController/${data.jointDataControllerId}/agreement?agreementId=${data.saveModel.agreementId}`
        );
    }

    async function addContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await post(`/processingActivity/JointDataController/${data.dataProcessorId}/contracts/${data.contractId}`, null);
    }

    async function removeContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await deleteHttp(`/processingActivity/JointDataController/${data.dataProcessorId}/contracts/${data.contractId}`);
    }
}

export function useValidateJointDataControllerStep(permissions: ProcessingActivityJointDataControllerPermissions) {
    const { translateString } = useTranslation();
    return (model: ProcessingActivityModel, responsibles: Array<UserSelectableItem> | undefined): Array<ValidationError> => {
        return getJointDataControllersErrors(model, translateString, responsibles, permissions);
    };
}

function getJointDataControllersErrors(
    model: ProcessingActivityModel,
    translateString: any,
    responsibles: Array<UserSelectableItem> | undefined,
    permissions: ProcessingActivityJointDataControllerPermissions
) {
    const result: Array<ValidationError> = [];

    if (permissions) {
        if (!model.disableJointDataControllers && !model.hasJointDataControllers) {
            result.push(new ValidationError("noDataSharingsJointDataControllers", translateString("atLeastOneSharingRequired")));
        }

        model.jointDataControllers.forEach((jointDataController, index) => {
            if (!jointDataController.dataControllerId) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("noControllerError")));
            }
            if (permissions.hasDataCategoriesPermission && jointDataController.dataCategories.length === 0) {
                result.push(new ValidationError(`${index}dataCategories`, translateString("dataCategoriesRequired")));
            }
            if (jointDataController.legalEntities.length === 0) {
                result.push(new ValidationError(`${index}companies`, translateString("noCompaniesForTransferChosenError")));
            }
            if (jointDataController.legalEntities.some((l) => l === jointDataController.dataControllerId)) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("dataControllerCantBeInCompanies")));
            }

            if (permissions.hasResponsiblePermission) {
                let user = responsibles?.find((x) => x.id === jointDataController.responsible);
                if (user && !user.active) {
                    result.push(new ValidationError(`${index}inactiveUser`, translateString("userDeactivated")));
                }
            }
        });
    }

    return result;
}
